"use client"

import { useState } from "react"
import { X, Copy, ExternalLink, StickyNote, User2, KeyRound, CheckCircle2, Loader2 } from "lucide-react"
import { GeistSans } from "geist/font/sans"
import { GeistMono } from "geist/font/mono"
import { formatOrderTableDate } from "@/lib/order-table-utils"
import {
  buildConnectionStatusHistoryEntries,
  formatBenefitEntry,
  getConnectionStatusDisplay,
  getConnectionStatusStageNum,
  getSeoMetricsForConnection,
  sortBenefitLinesLogoLinkFirst,
} from "@/lib/connection-display-utils"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"

const connectionStatusStyles: Record<string, string> = {
  Ready: "bg-green-100 text-green-800 border-green-200",
  Noticed: "bg-blue-100 text-blue-800 border-blue-200",
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Reminded: "bg-amber-100 text-amber-800 border-amber-200",
  Filled: "bg-indigo-100 text-indigo-800 border-indigo-200",
  Rejected: "bg-red-100 text-red-800 border-red-200",
  "Bad Apple": "bg-red-900 text-red-100 border-red-800",
  "Pending Approval": "bg-amber-50 text-amber-600 border-amber-200",
  "Approved by Client": "bg-blue-50 text-blue-600 border-blue-200",
  Complete: "bg-emerald-50 text-emerald-600 border-emerald-200",
  "Declined by Client": "bg-red-50 text-red-600 border-red-200",
  Created: "bg-slate-100 text-slate-800 border-slate-200",
  Reserve: "bg-violet-50 text-violet-700 border-violet-200",
}

const BENEFITS_BY_LEVEL: Record<string, string[]> = {
  Touchdown: ["Logo on event banner", "Social media mention", "Website listing"],
  Major: ["Logo on event banner", "Social media mention", "Website listing", "VIP passes (2)"],
  Friend: ["Website listing", "Thank you mention"],
  Roulette: ["Logo on event materials", "Social media shoutout", "VIP passes (4)"],
  Premier: ["Premier logo placement", "Speaking opportunity", "VIP passes (6)", "Booth space"],
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded border ${connectionStatusStyles[status] || "bg-gray-50 text-gray-600 border-gray-200"}`}
    >
      {status}
    </span>
  )
}

function formatStatusHistoryDateLabel(dateRaw: string): string {
  const s = String(dateRaw ?? "").trim()
  if (!s) return "—"
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? "—" : formatOrderTableDate(s)
}

interface ConnectionDetailsModalProps {
  open: boolean
  onClose: () => void
  connection: any
  orderId: string | number
  zsOppFee?: number
  onApprove?: () => void | Promise<void>
  /**
   * Client dashboard pending / rejections: hide Payment Details + Connection Details; SEO grid includes Traffic.
   */
  clientDashboardContext?: boolean
}

export function ConnectionDetailsModal({
  open,
  onClose,
  connection,
  orderId,
  zsOppFee = 150,
  onApprove,
  clientDashboardContext = false,
}: ConnectionDetailsModalProps) {
  const [approving, setApproving] = useState(false)
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  if (!connection) return null

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const conn = connection
  const connectionId = conn?.id != null ? `CON-${String(conn.id).padStart(3, "0")}` : "—"
  const status = getConnectionStatusDisplay(conn)
  const org = conn?.sponsorship_level?.opp?.org?.name ?? conn?.org?.name ?? "—"
  const opportunity = conn?.sponsorship_level?.opp?.name ?? conn?.sponsorship_level?.name ?? conn?.opportunity ?? "—"
  const levelName = conn?.sponsorship_level?.name ?? conn?.sponsorship_level?.opp?.sponsorship_level?.name ?? "—"
  const eventDate = conn?.sponsorship_level?.opp?.event_date ?? conn?.sponsorship_level?.opp?.start_date ?? conn?.event_date ?? null
  const eventDateFormatted = eventDate ? formatOrderTableDate(String(eventDate)) : "—"
  const duration = conn?.sponsorship_level?.opp?.duration ?? conn?.duration ?? "—"
  const sponsorshipNum =
    conn?.sponsorship_cost ??
    conn?.sponsorship_level?.cost ??
    conn?.sponsorship ??
    conn?.cost ??
    0
  const processingFeeNum =
    conn?.processing_fee != null && typeof conn.processing_fee === "number"
      ? conn.processing_fee
      : conn?.processing_fee_percent != null && typeof conn.processing_fee_percent === "number" && sponsorshipNum > 0
        ? (sponsorshipNum * conn.processing_fee_percent) / 100
        : 0
  const zsFeeNum = conn?.sponsorship_fee != null ? conn.sponsorship_fee : zsOppFee
  const total = sponsorshipNum + processingFeeNum + zsFeeNum
  const paid = !!(conn?.paid_time && String(conn.paid_time).trim())
  const paymentDate = conn?.paid_time ? formatOrderTableDate(String(conn.paid_time)) : "—"
  const pm = conn?.payments?.[0]?.payment_method ?? null
  const API_TO_PAYMENT_TYPE: Record<string, string> = {
    credit_card: "Credit Card", paypal: "Paypal", check: "Check",
  }
  const API_TO_SUB_TYPE: Record<string, string> = {
    payment_link: "Payment Link", invoice: "Invoice",
    phone_call: "Phone Call", paypal_email_address: "Paypal Email Address",
  }
  const displayType = pm?.type ? (API_TO_PAYMENT_TYPE[pm.type] || pm.type) : null
  const displaySubType = pm?.sub_type ? (API_TO_SUB_TYPE[pm.sub_type] || pm.sub_type) : null
  const { seoDa: da, seoDr: dr, seoTraffic: traffic, seoSpam: spam } = getSeoMetricsForConnection(conn)
  const apiBenefits = Array.isArray(conn?.sponsorship_level?.benefits) ? conn.sponsorship_level.benefits : []
  const benefits =
    (levelName && BENEFITS_BY_LEVEL[levelName])
      ? BENEFITS_BY_LEVEL[levelName]
      : apiBenefits
  const benefitsDisplayOrdered = sortBenefitLinesLogoLinkFirst(
    benefits.map((b: unknown) => formatBenefitEntry(b)).filter(Boolean)
  )
  const notesRaw = conn?.notes ?? conn?.connection_notes
  const notes =
    typeof notesRaw === "string"
      ? notesRaw
      : Array.isArray(notesRaw)
        ? notesRaw.map((n: any) => (typeof n === "object" && n?.content != null ? n.content : String(n))).filter(Boolean).join("\n\n") || "—"
        : notesRaw && typeof notesRaw === "object" && notesRaw?.content != null
          ? notesRaw.content
          : "—"
  const addedByRaw = conn?.added_by ?? conn?.created_by ?? conn?.assigned_to
  const addedBy =
    typeof addedByRaw === "string"
      ? addedByRaw
      : addedByRaw && typeof addedByRaw === "object" && (addedByRaw?.name ?? addedByRaw?.username)
        ? addedByRaw.name ?? addedByRaw.username
        : "—"
  const membershipRaw = conn?.membership ?? conn?.membership_info ?? null
  const opp = conn?.sponsorship_level?.opp ?? conn?.opportunity
  const membership =
    membershipRaw && (membershipRaw?.url || membershipRaw?.username || membershipRaw?.password)
      ? membershipRaw
      : opp?.membership_portal_url || opp?.membership_username || opp?.membership_password
        ? {
            url: opp?.membership_portal_url ?? null,
            username: opp?.membership_username ?? null,
            password: opp?.membership_password ?? null,
          }
        : null
  const urlCheckStatus = (conn?.url_check?.status ?? "").toLowerCase()
  const linkLive = urlCheckStatus === "active"
  const liveDate = conn?.url_check?.last_checked ?? conn?.url_check?.updated_at ?? null

  const statusHistoryDescriptions: Record<string, string> = {
    Created: "Connection created",
    "Pending Approval": "Opportunity identified and sent to client for review",
    "Approved by Client": "Client reviewed and approved the sponsorship opportunity. Opportunity is in fulfillment.",
    Complete: "Sponsorship fulfilled and link placement confirmed",
    "Declined by Client": "Client reviewed and declined this sponsorship opportunity",
    "Bad Apple": "Flagged and removed from fulfillment process",
    Ready: "Connection created and ready for processing",
    Noticed: "Opportunity identified and sent to client for review",
    Paid: "Client reviewed and approved the sponsorship opportunity",
    Reminded: "Sponsorship fulfilled and link placement confirmed",
    Filled: "Sponsorship completed",
    Rejected: "Client reviewed and declined this sponsorship opportunity",
    Reserve: "Connection held in reserve",
  }

  const connectionStageNum = getConnectionStatusStageNum(conn)
  const canApprove = (connectionStageNum === 0 || connectionStageNum === 6) && onApprove

  const handleApproveClick = () => {
    if (!canApprove) return
    setShowApproveConfirm(true)
  }

  const handleApproveConfirm = async () => {
    if (!canApprove) return
    setApproving(true)
    try {
      await onApprove?.()
      onClose()
    } catch {
      // Error handled by caller (toast), keep confirmation view open
    } finally {
      setApproving(false)
    }
  }
  const statusDateRaw = conn?.stage_updated_date ?? conn?.status_date ?? conn?.date_updated ?? ""
  const sortedEntries = buildConnectionStatusHistoryEntries(conn)
  const statusHistoryItems =
    sortedEntries.length > 0
      ? sortedEntries.map(({ status, dateRaw }) => ({
          status,
          date: formatStatusHistoryDateLabel(dateRaw),
          description: statusHistoryDescriptions[status] || "Status updated",
        }))
      : [
          {
            status,
            date: formatStatusHistoryDateLabel(statusDateRaw),
            description: statusHistoryDescriptions[status] || "Status updated",
          },
        ]

  return (
    <Dialog open={open} onOpenChange={(open) => !open && (setShowApproveConfirm(false), onClose())}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 [&>button]:hidden">
        <div
          data-connection-modal
          className={`${GeistSans.className} relative flex flex-col min-h-0 flex-1 overflow-hidden`}
        >
        <DialogHeader className="shrink-0 border-b border-border p-5 flex flex-row items-start justify-between space-y-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`${GeistMono.className} text-xs text-muted-foreground`}>{connectionId}</span>
              <StatusBadge status={status} />
            </div>
            <DialogTitle className="text-base font-semibold">{org}</DialogTitle>
            <p className="text-sm text-muted-foreground mt-0.5">{opportunity}</p>
          </div>
          <div className="flex items-center gap-3">
            {canApprove && (
              <Button
                size="sm"
                onClick={handleApproveClick}
                disabled={approving}
                className="gap-1.5"
              >
                {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Approve
              </Button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground -m-1.5"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <div className="p-5 space-y-6 overflow-y-auto min-h-0 flex-1">
          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">Status History</h4>
            <div className="relative pl-6">
              <div className="absolute left-[9px] top-2 bottom-2 w-px bg-border" />
              {statusHistoryItems.map((item, index) => (
                <div key={index} className="relative flex items-start gap-3 pb-4 last:pb-0">
                  <div
                    className={`absolute -left-6 top-1.5 w-[10px] h-[10px] rounded-full border-2 border-background ring-2 ring-background ${
                      item.status === "Rejected" || item.status === "Bad Apple" || item.status === "Declined by Client"
                        ? "bg-red-500"
                        : "bg-emerald-500"
                    }`}
                  />
                  <div className="flex-1 flex items-start justify-between">
                    <div>
                      <StatusBadge status={item.status} />
                      <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-3 mt-0.5">{item.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">Sponsorships & Costs</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 rounded-md p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sponsorship Level</span>
                  <span className="font-medium text-foreground">{levelName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Event Date</span>
                  <span className="text-foreground">{eventDateFormatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="text-foreground">{duration}</span>
                </div>
              </div>
              <div className="bg-muted/50 rounded-md p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sponsorship Cost</span>
                  <span className="text-foreground">${Number(sponsorshipNum).toLocaleString()}</span>
                </div>
                {processingFeeNum > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processing Fee{conn?.processing_fee_percent != null ? ` (${conn.processing_fee_percent}%)` : ""}</span>
                    <span className="text-foreground">${processingFeeNum.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ZS Fee</span>
                  <span className="text-foreground">${Number(zsFeeNum).toLocaleString()}</span>
                </div>
                <div className="flex justify-between pt-1.5 border-t border-dashed border-border">
                  <span className="font-medium text-foreground">Total</span>
                  <span className="font-bold text-foreground">${total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">SEO Metrics</h4>
            <div
              className={
                clientDashboardContext
                  ? "grid grid-cols-2 gap-3 sm:grid-cols-4"
                  : "flex items-center gap-4"
              }
            >
              <div className={`bg-muted/50 rounded-md px-4 py-2.5 text-center ${clientDashboardContext ? "" : "flex-1"}`}>
                <div className="text-lg font-bold text-foreground">{da != null ? da : "—"}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">DA</div>
              </div>
              <div className={`bg-muted/50 rounded-md px-4 py-2.5 text-center ${clientDashboardContext ? "" : "flex-1"}`}>
                <div className="text-lg font-bold text-foreground">{dr != null ? dr : "—"}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">DR</div>
              </div>
              {clientDashboardContext && (
                <div className="bg-muted/50 rounded-md px-3 py-2.5 text-center">
                  <div className="text-lg font-bold text-foreground break-all leading-tight">
                    {traffic != null && traffic !== "" ? traffic : "—"}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Traffic</div>
                </div>
              )}
              <div className={`bg-muted/50 rounded-md px-4 py-2.5 text-center ${clientDashboardContext ? "" : "flex-1"}`}>
                <div className="text-lg font-bold text-foreground">{spam != null ? `${spam}%` : "—"}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Spam</div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">
              {levelName ? `${levelName} Level Benefits` : "Level Benefits"}
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              {benefitsDisplayOrdered.map((text, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  {text}
                </li>
              ))}
            </ul>
          </section>

          {!clientDashboardContext && (
          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">Payment Details</h4>
            <div className="bg-muted/50 rounded-md p-3 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${
                    paid ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"
                  }`}
                >
                  {paid ? "Paid" : "Unpaid"}
                </span>
              </div>
              {displayType && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Type</span>
                  <span className="text-foreground">{displayType}</span>
                </div>
              )}
              {displaySubType && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Method</span>
                  <span className="text-foreground">{displaySubType}</span>
                </div>
              )}
              {pm?.payment_url && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment URL</span>
                  <a href={pm.payment_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[60%] text-right">{pm.payment_url}</a>
                </div>
              )}
              {pm?.invoice_url && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice URL</span>
                  <a href={pm.invoice_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[60%] text-right">{pm.invoice_url}</a>
                </div>
              )}
              {(pm?.first_name || pm?.last_name) && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contact</span>
                  <span className="text-foreground">{[pm.first_name, pm.last_name].filter(Boolean).join(" ")}</span>
                </div>
              )}
              {pm?.phone_number && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="text-foreground">{pm.phone_number}</span>
                </div>
              )}
              {pm?.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="text-foreground">{pm.email}</span>
                </div>
              )}
              {pm?.check_written_to && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Check Written To</span>
                  <span className="text-foreground">{pm.check_written_to}</span>
                </div>
              )}
              {pm?.address && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mailing Address</span>
                  <span className="text-foreground text-right">
                    <span className="block">{pm.address}</span>
                    {(pm.city || pm.state || pm.zip_code) && (
                      <span className="block">{[pm.city, pm.state, pm.zip_code].filter(Boolean).join(", ")}</span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reference #</span>
                <span className="text-muted-foreground text-xs">(coming soon)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment Date</span>
                <span className="text-foreground">{paymentDate}</span>
              </div>
              <div className="pt-2 border-t border-dashed border-border mt-1">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline disabled:opacity-50"
                  disabled
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Payment attachment (coming soon)
                </button>
              </div>
            </div>
          </section>
          )}

          {linkLive && liveDate && (
            <section>
              <h4 className="text-sm font-semibold text-foreground mb-3">Link Status History</h4>
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded border bg-emerald-50 text-emerald-600 border-emerald-200">
                      Live
                    </span>
                    <p className="text-sm text-muted-foreground mt-1">Link verified and confirmed live</p>
                  </div>
                  <span className="text-sm text-muted-foreground">{formatOrderTableDate(String(liveDate))}</span>
                </div>
              </div>
            </section>
          )}

          {!clientDashboardContext && (
          <section>
            <h4 className="text-sm font-semibold text-foreground mb-3">Connection Details</h4>
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  <StickyNote className="w-3 h-3" />
                  Notes
                </div>
                <p className="text-sm text-foreground">{notes}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  <User2 className="w-3 h-3" />
                  Added By
                </div>
                <p className="text-sm text-foreground">{addedBy}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  <KeyRound className="w-3 h-3" />
                  Membership Info
                </div>
                <div className="bg-muted/50 rounded-md p-3 space-y-2 text-sm">
                  {membership && (membership?.url || membership?.username || membership?.password) ? (
                    <>
                    {membership?.url && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground shrink-0">URL</span>
                        <a
                          href={membership.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-xs truncate max-w-[250px] flex items-center gap-0.5"
                        >
                          {String(membership.url).replace(/^https?:\/\//, "")}
                          <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                      </div>
                    )}
                    {membership?.username && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground shrink-0">Username</span>
                        <div className="flex items-center gap-1.5">
                          <code className="text-xs bg-background px-1.5 py-0.5 rounded border">{membership.username}</code>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(membership.username)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Copy username"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    {membership?.password && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground shrink-0">Password</span>
                        <div className="flex items-center gap-1.5">
                          <code className="text-xs bg-background px-1.5 py-0.5 rounded border">{membership.password}</code>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(membership.password)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Copy password"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </div>
              </div>
            </div>
          </section>
          )}

        </div>

        {showApproveConfirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 p-6">
            <div className="bg-card border rounded-lg p-6 max-w-md w-full shadow-lg">
              <h3 className="text-lg font-semibold">Approve Client Order Connection</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Approving this Client Order Connection will send an email to the client and update the status to Pending Approval.
              </p>
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowApproveConfirm(false)}
                  disabled={approving}
                >
                  Cancel
                </Button>
                <Button onClick={handleApproveConfirm} disabled={approving} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                  {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Approve
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
